import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AuroraBackground } from './ui/aurora-background';
import { SpotlightEffect } from './ui/spotlight-effect';

export const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Mouse parallax effect (desktop only)
  useEffect(() => {
    if (window.innerWidth <= 768) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      requestAnimationFrame(() => {
        const x = (e.clientX / window.innerWidth - 0.5) * 2;
        const y = (e.clientY / window.innerHeight - 0.5) * 2;
        setMousePosition({ x, y });
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll-triggered fade-out
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const progress = Math.min(scrollY / (windowHeight * 0.5), 1);
      setScrollProgress(progress);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-[#0a0a0a] overflow-hidden">
      {/* Layer 1: Aurora Gradient Mesh */}
      <AuroraBackground parallaxOffset={mousePosition} />
      
      {/* Layer 2: Spotlight Glow */}
      <SpotlightEffect parallaxOffset={mousePosition} />

      {/* Layer 5: Content */}
      <div 
        className="container-professional text-center py-20 relative z-10"
        style={{
          opacity: 1 - scrollProgress * 0.8,
          transform: `translateY(${scrollProgress * 50}px)`,
          filter: `blur(${scrollProgress * 3}px)`,
          transition: 'filter 0.3s ease-out',
        }}
      >
        {/* Proof Pill */}
        <div 
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] mb-8 opacity-0 hover:border-primary/30 transition-colors duration-300"
          style={{
            animation: isLoaded ? 'fade-in 0.6s ease-out forwards' : 'none',
            animationDelay: '0s',
          }}
        >
          <span className="relative w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-emerald-500 animate-pulse" />
            <span className="absolute inset-0 rounded-full bg-emerald-500/50 animate-ping" style={{ animationDuration: '2s' }} />
          </span>
          <span className="text-xs text-[rgba(255,255,255,0.6)] font-medium tracking-wide">
            Trusted by 15+ companies across Nordics
          </span>
        </div>

        {/* Headline - Outcome focused */}
        <h1 
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight opacity-0"
          style={{ 
            letterSpacing: '-0.02em',
            animation: isLoaded ? 'fade-up 0.6s ease-out forwards' : 'none',
            animationDelay: '0.1s',
          }}
        >
          From Concept to Launch
          <br />
          <span className="text-primary">in 12 Weeks</span>
        </h1>
        
        {/* Subheadline with metric */}
        <p 
          className="text-lg md:text-xl text-[rgba(255,255,255,0.65)] max-w-2xl mx-auto mb-10 leading-relaxed opacity-0"
          style={{
            animation: isLoaded ? 'fade-in 0.8s ease-out forwards' : 'none',
            animationDelay: '0.4s',
          }}
        >
          We build custom web apps and mobile apps with{' '}
          <span className="text-white font-medium">60% less technical debt</span>.
          <br className="hidden md:block" />
          No scope creep. No communication gaps.
        </p>
        
        {/* CTA Button */}
        <div 
          className="flex justify-center mb-12 opacity-0"
          style={{
            animation: isLoaded ? 'scale-in 0.6s ease-out forwards' : 'none',
            animationDelay: '0.6s',
          }}
        >
          <Link to="/contact">
            <button 
              className="group relative h-12 px-8 text-sm bg-primary hover:bg-[#00d4e6] text-white font-semibold rounded-lg transition-all duration-300 hover:transform hover:-translate-y-1 flex items-center gap-2 overflow-hidden"
              style={{
                boxShadow: '0 4px 24px -6px hsl(190 100% 50% / 0.5), 0 0 0 1px hsl(190 100% 50% / 0.1)',
              }}
            >
              <span className="relative z-10">View the Roadmap</span>
              <ArrowRight className="w-4 h-4 relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Link>
        </div>
        
        {/* Service Tags - Split offerings */}
        <div 
          className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8 opacity-0"
          style={{
            animation: isLoaded ? 'fade-in 0.8s ease-out forwards' : 'none',
            animationDelay: '0.8s',
          }}
        >
          <span className="text-xs text-[rgba(255,255,255,0.5)] uppercase tracking-wider font-mono flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            Enterprise Engineering
          </span>
          <span className="hidden sm:block w-px h-4 bg-[rgba(255,255,255,0.2)]" />
          <span className="text-xs text-[rgba(255,255,255,0.5)] uppercase tracking-wider font-mono flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Business Essentials
          </span>
        </div>
      </div>
    </section>
  );
};
